import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import type { TurnstileAction } from "@/lib/api/turnstile";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          action?: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          language?: string;
          size?: "normal" | "compact" | "flexible";
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoaded?: () => void;
  }
}

/**
 * Whether a Turnstile widget will actually render.
 *
 * Exported so a form can require a token *only* where one can be obtained. Dev
 * and any unconfigured environment have no site key, no widget, and therefore
 * no token — gating submission on one there would block every form.
 */
export const TURNSTILE_ENABLED = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);

/**
 * Turnstile's own language codes for the ten locales this site serves.
 *
 * Left unset, the widget picks the *browser's* language, so a buyer reading the
 * Turkish or Arabic site is asked to confirm they are human in English. Every
 * one of the ten is on Cloudflare's supported list; the fuller codes are used
 * where that list only carries a regional variant.
 */
const TURNSTILE_LANGUAGES: Record<string, string> = {
  en: "en",
  tr: "tr",
  de: "de",
  es: "es",
  ru: "ru",
  ar: "ar-eg",
  ja: "ja",
  ko: "ko",
  zh: "zh-cn",
  fa: "fa-ir",
};

export interface TurnstileWidgetRef {
  reset: () => void;
}

export interface TurnstileWidgetProps {
  /**
   * Which surface this widget guards. Baked into the token by Cloudflare and
   * checked server-side against the endpoint being called, so a token minted
   * here cannot be spent somewhere else. Required rather than optional: an
   * un-actioned token is one the server cannot scope.
   */
  action: TurnstileAction;
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  /**
   * Defaults to "light" rather than Turnstile's own "auto".
   *
   * "auto" follows the viewer's OS colour scheme, but this site has no dark
   * mode — no `darkMode` in the Tailwind config, no theme provider, no
   * `prefers-color-scheme` anywhere in the stylesheets. Every surface is light
   * all the time, so "auto" simply meant that anyone whose system was set to
   * dark got a black widget sitting in a white form (reported from production,
   * VARS-116). Pass "dark" explicitly on the few dark cards.
   */
  theme?: "light" | "dark" | "auto";
  className?: string;
}

export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
  function TurnstileWidget(
    { action, onSuccess, onError, onExpire, theme = "light", className },
    ref,
  ) {
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    const { lang } = useI18n();
    const language = TURNSTILE_LANGUAGES[lang] ?? "auto";
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);

    /*
     * The callbacks are held in refs and deliberately kept OUT of the render
     * effect's dependencies.
     *
     * Every caller passes inline arrows (`onSuccess={(t) => setToken(t)}`), so
     * their identity changes on every parent render. With them in the deps, the
     * sequence was: render the widget -> Turnstile solves -> onSuccess -> parent
     * setState -> parent re-renders -> new callback identity -> effect re-runs ->
     * remove() and render() again -> solves again -> forever. A visible,
     * permanently reloading widget, and a fresh token every cycle.
     *
     * Refs give the effect a stable identity to depend on while still calling
     * whatever the latest render passed down.
     */
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);
    const onExpireRef = useRef(onExpire);
    useEffect(() => {
      onSuccessRef.current = onSuccess;
      onErrorRef.current = onError;
      onExpireRef.current = onExpire;
    });

    const clearToken = useCallback(() => onSuccessRef.current(""), []);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
            // Turnstile tokens are single-use: the one the parent is holding has
            // just been spent, and a fresh challenge is now in flight. Clear it
            // immediately rather than leaving a spent token in state where a
            // quick second submission would send it and be rejected as a
            // duplicate — which looks to the buyer like the form failing twice.
            clearToken();
          }
        },
      }),
      [clearToken],
    );

    // Load the API script once. Not dependent on the callbacks, for the reason
    // above — this used to re-run on every parent render too.
    useEffect(() => {
      if (!siteKey) {
        // No widget will render, so tell the parent there is no token to wait
        // for. TURNSTILE_ENABLED is what stops that being read as "unsolved".
        clearToken();
        return;
      }

      const scriptId = "cf-turnstile-script";
      let script = document.getElementById(scriptId) as HTMLScriptElement | null;

      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.onload = () => setScriptLoaded(true);
        document.head.appendChild(script);
      } else if (window.turnstile) {
        setScriptLoaded(true);
      } else {
        script.addEventListener("load", () => setScriptLoaded(true));
      }
    }, [siteKey, clearToken]);

    useEffect(() => {
      if (!scriptLoaded || !siteKey || !containerRef.current || !window.turnstile) {
        return;
      }

      // Cleanup any previous widget before rendering
      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
      }

      try {
        const id = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme,
          language,
          // Fills the width it is given instead of a fixed 300px box, so the
          // widget lines up with the fields above it rather than sitting
          // narrower than the form.
          size: "flexible",
          callback: (token: string) => onSuccessRef.current(token),
          "error-callback": () => {
            // A failed challenge must not leave a stale token behind, or the
            // form would submit one the server has already refused.
            onSuccessRef.current("");
            onErrorRef.current?.();
          },
          "expired-callback": () => {
            onSuccessRef.current("");
            onExpireRef.current?.();
          },
        });
        widgetIdRef.current = id;
      } catch (err) {
        console.error("[Turnstile] Render error:", err);
      }

      return () => {
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // ignore
          }
          widgetIdRef.current = null;
        }
      };
      // Only values that genuinely change what is rendered belong here.
    }, [scriptLoaded, siteKey, action, theme, language]);

    if (!siteKey) {
      return null;
    }

    // The data attributes are the only externally visible proof that a form is
    // guarded and under which action. e2e/turnstile.spec.ts asserts on them, so
    // a form that posts to a verified endpoint without a widget fails the suite
    // instead of failing silently in production the day the secret is set.
    return (
      <div
        ref={containerRef}
        data-testid="turnstile"
        data-action={action}
        className={className ?? "my-2 flex justify-center"}
      />
    );
  },
);
